// frontend/src/components/dfs/MatchBreakdownModal.tsx
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { ApiMatchInfo, ApiPlayerInMatchWithScore, fetchPlayersForMatchInContest, fetchMatchBreakdown } from '@/api/dfs';
import SafeViewportOverlay from '@/components/common/SafeViewportOverlay';
import { formatPlayerDisplayName } from '@/utils/formatting';
import { getAbbreviation } from '@/utils/teams';
import { Icon } from 'lucide-react';
import { soccerBall } from '@lucide/lab';
import { useAuth } from '@/contexts/AuthContext';
import { parseISO, isPast } from 'date-fns';
import AvailabilityBadge from '@/components/common/AvailabilityBadge';
import InjuryBadge from '@/components/common/InjuryBadge';
import { useExitTransition } from '@/hooks/useExitTransition';
import { useBodyScrollLock } from '@/hooks/useBodyScrollLock';
import { useAppBackButtonInterceptor } from '@/hooks/useAppBackButtonInterceptor';
import { userFacingError } from '@/utils/userFacingError';

const logger = console;

interface MatchScoreInfo {
  home_score: number;
  away_score: number;
  match_clock: string;
}

const TERMINAL_MATCH_STATUS_CODES = new Set(['FT', 'PPD', 'CANC']);
const EXCEPTION_MATCH_STATUS_CODES = new Set(['PPD', 'CANC', 'SUSP', 'DLY', 'ABD']);
const EMPTY_MATCH_PLAYERS: ApiPlayerInMatchWithScore[] = [];

interface MatchBreakdownModalProps {
  isOpen: boolean;
  onClose: () => void;
  match: ApiMatchInfo | null;
  contestId: number | null;
  /** Season-long (non-contest) mode: the modal fetches the match itself. */
  leagueMatchId?: number | null;
  /** Season-long league context: rows carry that league's ownership label
   *  (owner team / Free Agent) so a match can be actioned from the
   *  breakdown. Omitted in DFS mode. */
  leagueId?: number | null;
  /** The viewer's own team in `leagueId`, to label their players "Your Team". */
  myTeamId?: number | null;
  /** Bump after a roster transaction to silently refetch ownership labels
   *  (the list lives in local state, not react-query). */
  refreshKey?: number;
  initialScoreInfo?: MatchScoreInfo | null;
  onPlayerScoreBreakdownClick?: (player: ApiPlayerInMatchWithScore, matchId: number) => void;
  onPlayerProfileClick?: (player: ApiPlayerInMatchWithScore) => void;
}

const formatStat = (value: number | null | undefined, decimals: number = 1): string => {
  if (value === null || value === undefined || isNaN(value)) return '-';
  return value.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};

// Compact player row used in both desktop two-column and mobile single-column
// League ownership label for a breakdown row. Null only in DFS mode (no
// league context). Every unrostered player reads "Free Agent" - waivers are
// not a separate label here (both mean "not rostered"), and never blank,
// which would look like a data error (owner 2026-08-22). The profile modal's
// Add/Claim buttons are still gated on the backend's availability status, so
// a participant outside the league's pool shows the label but cannot be
// added from here.
const leagueOwnershipLabel = (
  player: ApiPlayerInMatchWithScore,
  leagueId: number | null | undefined,
  myTeamId: number | null | undefined,
): { text: string; className: string } | null => {
  if (leagueId == null) return null;
  if (player.owner_team_id != null) {
    if (myTeamId != null && player.owner_team_id === myTeamId) {
      return { text: 'Your Team', className: 'text-green-400' };
    }
    return { text: player.owner_team_name || 'Rostered', className: 'text-gray-400' };
  }
  return { text: 'Free Agent', className: 'text-accent-light-blue' };
};

const CompactPlayerRow: React.FC<{
  player: ApiPlayerInMatchWithScore;
  isMatchActive: boolean;
  canViewBreakdown: boolean;
  showSalary: boolean;
  leagueId?: number | null;
  myTeamId?: number | null;
  onRowClick?: () => void;
  onScoreClick: () => void;
}> = ({ player, isMatchActive, canViewBreakdown, showSalary, leagueId, myTeamId, onRowClick, onScoreClick }) => {
  const displayName = formatPlayerDisplayName(player);
  const ownership = leagueOwnershipLabel(player, leagueId, myTeamId);
  const isRowInteractive = onRowClick != null;

  const handleRowKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (isRowInteractive && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      onRowClick?.();
    }
  };

  // stopPropagation as well as preventDefault: the chip sits inside the row's
  // own keydown handler, so Enter would otherwise open the profile too.
  const handleScoreKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (canViewBreakdown && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      event.stopPropagation();
      onScoreClick();
    }
  };

  return (
    <div
      className={`min-h-14 border-b border-accent-light-blue/10 bg-primary-dark/45 odd:bg-primary/25 last:border-b-0 ${
        isRowInteractive
          ? 'group cursor-pointer transition-colors duration-150 hover:bg-surface-raised/70 active:bg-surface-raised/70'
          : ''
      }`}
      onClick={onRowClick}
      onKeyDown={handleRowKeyDown}
      role={isRowInteractive ? 'button' : undefined}
      tabIndex={isRowInteractive ? 0 : undefined}
    >
      <div className="flex min-h-14 items-center gap-2 px-2.5 py-1.5 md:px-3">
        <span className="inline-flex h-8 w-10 flex-shrink-0 items-center justify-center rounded-md bg-accent-yellow/15 text-badge font-bold text-accent-yellow ring-1 ring-inset ring-accent-yellow/25">
          {player.primary_position || '-'}
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-1.5">
            <p className="truncate text-sm font-semibold leading-tight text-gray-100 group-hover:text-white group-active:text-white" title={displayName}>
              {displayName}
            </p>
            {isMatchActive && (player.goals_in_match ?? 0) > 0 && (
              <span
                className="flex flex-shrink-0 items-center gap-0.5 text-accent-yellow"
                aria-label={`${player.goals_in_match} goal${player.goals_in_match === 1 ? '' : 's'}`}
              >
                {Array.from({ length: player.goals_in_match! }).map((_, i) => (
                  <Icon key={i} iconNode={soccerBall} size={12} aria-hidden="true" />
                ))}
              </span>
            )}
          </div>
          <div className="mt-1 flex min-h-[18px] items-center gap-1.5 overflow-hidden">
            <span className="flex-shrink-0">
              <AvailabilityBadge status={player.availability_status} isLocked={isMatchActive} />
            </span>
            <InjuryBadge
              status={player.injury_status}
              details={player.injury_details}
              size={10}
              className="flex-shrink-0"
            />
            {ownership && (
              <span
                className={`min-w-0 truncate text-micro font-semibold ${ownership.className}`}
                title={ownership.text}
              >
                {ownership.text}
              </span>
            )}
          </div>
        </div>

        <div className="flex flex-shrink-0 items-center gap-2 md:gap-3">
          {showSalary && (
            <div className="min-w-[44px] text-right">
              <p className="text-xs font-semibold tabular-nums text-gray-200">${formatStat(player.dfs_salary, 0)}</p>
              <p className="mt-0.5 text-micro font-semibold uppercase tracking-wide text-gray-500">Sal</p>
            </div>
          )}
          {isMatchActive ? (
            <div
              className={`min-w-[48px] rounded-md px-1.5 py-1 text-right transition-colors duration-150 ${
                canViewBreakdown
                  ? 'group/score cursor-pointer bg-accent-yellow/10 ring-1 ring-inset ring-accent-yellow/40 hover:bg-accent-yellow hover:ring-accent-yellow active:bg-accent-yellow active:ring-accent-yellow focus:outline-none focus-visible:ring-2 focus-visible:ring-accent-yellow'
                  : ''
              }`}
              onClick={(event) => {
                if (canViewBreakdown) {
                  event.stopPropagation();
                  onScoreClick();
                }
              }}
              onKeyDown={handleScoreKeyDown}
              role={canViewBreakdown ? 'button' : undefined}
              tabIndex={canViewBreakdown ? 0 : undefined}
              aria-label={canViewBreakdown ? `View score breakdown for ${displayName}` : undefined}
              title={canViewBreakdown ? 'View Score Breakdown' : undefined}
            >
              <p className={`text-lg font-heading font-bold leading-none tabular-nums transition-colors duration-150 ${
                canViewBreakdown
                  ? 'text-accent-yellow group-hover/score:text-primary-dark group-active/score:text-primary-dark'
                  : 'text-gray-300'
              }`}>
                {formatStat(player.match_fantasy_points, 1)}
              </p>
              <p className={`mt-1 text-micro font-semibold uppercase tracking-wide transition-colors duration-150 ${
                canViewBreakdown
                  ? 'text-gray-400 group-hover/score:text-primary-dark/75 group-active/score:text-primary-dark/75'
                  : 'text-gray-400'
              }`}>FPTS</p>
            </div>
          ) : (
            <>
              <div className="min-w-[38px] text-right">
                <p className="text-sm font-semibold tabular-nums text-gray-300">{formatStat(player.fantasy_points, 0)}</p>
                <p className="mt-0.5 text-micro font-semibold uppercase tracking-wide text-gray-500">PTS</p>
              </div>
              <div className="min-w-[42px] text-right">
                <p className="text-base font-bold tabular-nums text-accent-yellow">{formatStat(player.fppg, 1)}</p>
                <p className="mt-0.5 text-micro font-semibold uppercase tracking-wide text-gray-400">FPPG</p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const MatchBreakdownModal: React.FC<MatchBreakdownModalProps> = ({
  isOpen,
  onClose,
  match,
  contestId,
  leagueMatchId,
  leagueId = null,
  myTeamId = null,
  refreshKey = 0,
  initialScoreInfo,
  onPlayerScoreBreakdownClick,
  onPlayerProfileClick,
}) => {
  const { token } = useAuth();
  const [matchPlayers, setMatchPlayers] = useState<ApiPlayerInMatchWithScore[]>([]);
  const [isLoadingPlayers, setIsLoadingPlayers] = useState<boolean>(false);
  const [playersError, setPlayersError] = useState<string | null>(null);
  const [selectedTeamId, setSelectedTeamId] = useState<number | null>(null); // Mobile toggle
  // League (non-contest) mode: the modal fetches the match itself.
  const [fetchedMatchResult, setFetchedMatchResult] = useState<{
    matchId: number;
    match: ApiMatchInfo;
  } | null>(null);
  const [requestVersion, setRequestVersion] = useState(0);
  const [requestStateKey, setRequestStateKey] = useState<string | null>(null);
  const currentRequestKey = leagueMatchId != null
    ? `league:${leagueMatchId}:${leagueId ?? 'none'}:${requestVersion}`
    : match && contestId != null
      ? `contest:${contestId}:${match.match_id}:${requestVersion}`
      : null;
  const requestStateIsCurrent = currentRequestKey != null
    && requestStateKey === currentRequestKey;
  const visibleMatchPlayers = requestStateIsCurrent ? matchPlayers : EMPTY_MATCH_PLAYERS;
  const visiblePlayersError = requestStateIsCurrent ? playersError : null;
  // A new selection renders as loading even before its effect runs, so no
  // frame can expose the previous match's players or error.
  const visibleIsLoading = currentRequestKey != null
    && (!requestStateIsCurrent || isLoadingPlayers);
  const fetchedMatch = fetchedMatchResult && fetchedMatchResult.matchId === leagueMatchId
    ? fetchedMatchResult.match
    : null;
  // In season-long mode a card preview supplies the shell immediately, then
  // the authoritative response replaces it. DFS mode already receives the
  // complete match object from its parent.
  const effectiveMatch = leagueMatchId != null
    ? (fetchedMatch ?? match)
    : (match ?? (currentRequestKey == null ? fetchedMatchResult?.match ?? null : null));
  const showSalary = contestId != null; // no salary concept in season-long
  const hasFetchedSeasonMatch = fetchedMatch !== null;
  const seasonLongStatusCode = fetchedMatch?.match_status_code?.toUpperCase() ?? 'NS';

  useEffect(() => {
    let cancelled = false;

    if (isOpen && match && contestId) {
      // --- DFS contest mode (works logged-out; token sent when present) ---
      setMatchPlayers([]);
      setPlayersError(null);
      setIsLoadingPlayers(true);
      setRequestStateKey(currentRequestKey);
      setSelectedTeamId(match.match_home_team_id); // Default to home

      fetchPlayersForMatchInContest(contestId, match.match_id, token)
        .then(data => {
          if (cancelled) return;
          setMatchPlayers(data);
          logger.log(`[MatchBreakdownModal] Fetched ${data.length} players for C:${contestId} M:${match.match_id}`);
        })
        .catch(err => {
          if (cancelled) return;
          logger.error(`[MatchBreakdownModal] Error fetching players:`, err);
          setPlayersError(userFacingError(err, 'Failed to load players for this match.'));
        })
        .finally(() => {
          if (!cancelled) setIsLoadingPlayers(false);
        });
    } else if (isOpen && leagueMatchId != null) {
      // --- Season-long (non-contest) mode: fetch match + players together ---
      setMatchPlayers([]);
      setFetchedMatchResult(null);
      setPlayersError(null);
      setIsLoadingPlayers(true);
      setRequestStateKey(currentRequestKey);
      setSelectedTeamId(match?.match_home_team_id ?? null);

      fetchMatchBreakdown(leagueMatchId, token, leagueId)
        .then(({ match: m, players }) => {
          if (cancelled) return;
          setFetchedMatchResult({ matchId: leagueMatchId, match: m });
          setMatchPlayers(players);
          setSelectedTeamId(m.match_home_team_id);
          logger.log(`[MatchBreakdownModal] League breakdown: ${players.length} players for M:${leagueMatchId}`);
        })
        .catch(err => {
          if (cancelled) return;
          logger.error(`[MatchBreakdownModal] Error fetching league breakdown:`, err);
          setPlayersError(userFacingError(err, 'Failed to load players for this match.'));
        })
        .finally(() => {
          if (!cancelled) setIsLoadingPlayers(false);
        });
    } else if (!isOpen) {
      setMatchPlayers([]);
      setPlayersError(null);
      setIsLoadingPlayers(false);
      setSelectedTeamId(null);
      setRequestStateKey(null);
    }

    return () => {
      cancelled = true;
    };
  }, [
    isOpen,
    match,
    contestId,
    leagueMatchId,
    leagueId,
    token,
    requestVersion,
    currentRequestKey,
  ]);

  // Silent refetch after a roster transaction (add/drop/claim/trade fired
  // from a profile opened off this list). Keeps the last good response on
  // screen; only the ownership labels change. Skips the mount pass.
  const lastRefreshKeyRef = useRef(refreshKey);
  useEffect(() => {
    if (refreshKey === lastRefreshKeyRef.current) return;
    lastRefreshKeyRef.current = refreshKey;
    if (!isOpen || leagueMatchId == null || !hasFetchedSeasonMatch) return;
    let cancelled = false;
    fetchMatchBreakdown(leagueMatchId, token, leagueId)
      .then(({ match: refreshedMatch, players }) => {
        if (cancelled) return;
        setFetchedMatchResult({ matchId: leagueMatchId, match: refreshedMatch });
        setMatchPlayers(players);
      })
      .catch(err => {
        logger.warn('[MatchBreakdownModal] Post-transaction refresh failed:', err);
      });
    return () => {
      cancelled = true;
    };
  }, [refreshKey, isOpen, leagueMatchId, leagueId, token, hasFetchedSeasonMatch]);

  // Season-long live detail stays current while open. The initial load owns
  // the visible loading/error state; background refreshes preserve the last
  // good response and stop automatically at a terminal status.
  useEffect(() => {
    if (!isOpen || leagueMatchId == null || !hasFetchedSeasonMatch) return;
    if (TERMINAL_MATCH_STATUS_CODES.has(seasonLongStatusCode)) return;

    let cancelled = false;
    let inFlight = false;
    const intervalId = window.setInterval(() => {
      if (inFlight) return;
      inFlight = true;
      fetchMatchBreakdown(leagueMatchId, token, leagueId)
        .then(({ match: refreshedMatch, players }) => {
          if (cancelled) return;
          setFetchedMatchResult({
            matchId: leagueMatchId,
            match: refreshedMatch,
          });
          setMatchPlayers(players);
        })
        .catch(err => {
          logger.warn('[MatchBreakdownModal] Background refresh failed:', err);
        })
        .finally(() => {
          inFlight = false;
        });
    }, 30_000);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
    };
  }, [
    isOpen,
    leagueMatchId,
    leagueId,
    token,
    hasFetchedSeasonMatch,
    seasonLongStatusCode,
  ]);

  const isMatchActive = useMemo(() => {
    if (!effectiveMatch?.match_date) return false;
    return isPast(parseISO(effectiveMatch.match_date));
  }, [effectiveMatch]);

  // Live score: prefer ESPN fields on the players, then initialScoreInfo,
  // then the match object itself (season-long carries it on the match).
  const liveScore = useMemo<MatchScoreInfo | null>(() => {
    const playerWithScore = visibleMatchPlayers.find(
      p => p.home_score != null && p.away_score != null && p.match_clock != null
    );
    if (playerWithScore) {
      return {
        home_score: playerWithScore.home_score ?? 0,
        away_score: playerWithScore.away_score ?? 0,
        match_clock: playerWithScore.match_clock ?? '',
      };
    }
    if (initialScoreInfo) return initialScoreInfo;
    if (effectiveMatch && effectiveMatch.home_score != null && effectiveMatch.away_score != null) {
      return {
        home_score: effectiveMatch.home_score,
        away_score: effectiveMatch.away_score,
        match_clock: effectiveMatch.match_clock ?? '',
      };
    }
    return null;
  }, [visibleMatchPlayers, initialScoreInfo, effectiveMatch]);

  const { homePlayers, awayPlayers } = useMemo(() => {
    if (!visibleMatchPlayers.length || !effectiveMatch) return { homePlayers: [], awayPlayers: [] };

    const sortFn = (a: ApiPlayerInMatchWithScore, b: ApiPlayerInMatchWithScore) => {
      if (isMatchActive) {
        const diff = (b.match_fantasy_points ?? -Infinity) - (a.match_fantasy_points ?? -Infinity);
        if (diff !== 0) return diff;
      } else {
        const diff = (b.dfs_salary ?? -Infinity) - (a.dfs_salary ?? -Infinity);
        if (diff !== 0) return diff;
      }
      const salaryDiff = (b.dfs_salary ?? 0) - (a.dfs_salary ?? 0);
      if (salaryDiff !== 0) return salaryDiff;
      return formatPlayerDisplayName(a).localeCompare(formatPlayerDisplayName(b));
    };

    const teamForMatch = (player: ApiPlayerInMatchWithScore) =>
      showSalary ? player.current_team_id : player.match_team_id;
    const home = visibleMatchPlayers.filter(p => teamForMatch(p) === effectiveMatch.match_home_team_id);
    const away = visibleMatchPlayers.filter(p => teamForMatch(p) === effectiveMatch.match_away_team_id);
    home.sort(sortFn);
    away.sort(sortFn);
    return { homePlayers: home, awayPlayers: away };
  }, [visibleMatchPlayers, effectiveMatch, isMatchActive, showSalary]);

  // Mobile: players for the selected team
  const mobileDisplayedPlayers = useMemo(() => {
    if (!selectedTeamId || !effectiveMatch) return [];
    if (selectedTeamId === effectiveMatch.match_home_team_id) return homePlayers;
    return awayPlayers;
  }, [selectedTeamId, effectiveMatch, homePlayers, awayPlayers]);

  const { render, closing } = useExitTransition(isOpen);
  useBodyScrollLock(render);
  useAppBackButtonInterceptor(render, onClose);
  useEffect(() => {
    if (!render) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [render, onClose]);

  if (!render) return null;

  const homeAbbr = effectiveMatch
    ? getAbbreviation(effectiveMatch.match_home_team_name)
    : '—';
  const awayAbbr = effectiveMatch
    ? getAbbreviation(effectiveMatch.match_away_team_name)
    : '—';

  const canPlayerViewBreakdown = () => {
    if (!onPlayerScoreBreakdownClick) return false;
    return isMatchActive;
  };

  const effectiveStatusCode = effectiveMatch?.match_status_code?.toUpperCase() ?? 'NS';
  const isLiveStatus = effectiveStatusCode === 'LIVE';
  const isExceptionalStatus = EXCEPTION_MATCH_STATUS_CODES.has(effectiveStatusCode);

  const handlePlayerRowClick = (player: ApiPlayerInMatchWithScore) => {
    if (onPlayerScoreBreakdownClick && effectiveMatch?.match_id && isMatchActive) {
      onPlayerScoreBreakdownClick(player, effectiveMatch.match_id);
    }
  };

  const renderColumnHeader = (teamAbbr: string, mobile = false) => (
    <div className="flex items-center justify-between">
      <span className="text-badge font-bold uppercase tracking-wider text-accent-light-blue">
        {mobile ? 'Players' : teamAbbr}
      </span>
      <span className="text-micro font-semibold uppercase tracking-wider text-gray-500">
        {isMatchActive ? 'Match performance' : 'Season form'}
      </span>
    </div>
  );

  const renderPlayerList = (players: ApiPlayerInMatchWithScore[]) => (
    <>
      {players.map(player => (
        <CompactPlayerRow
          key={player.id}
          player={player}
          isMatchActive={isMatchActive}
          canViewBreakdown={canPlayerViewBreakdown()}
          showSalary={showSalary}
          leagueId={leagueId}
          myTeamId={myTeamId}
          onRowClick={onPlayerProfileClick ? () => onPlayerProfileClick(player) : undefined}
          onScoreClick={() => handlePlayerRowClick(player)}
        />
      ))}
      {players.length === 0 && (
        <p className="text-gray-500 italic text-caption text-center py-4">No players</p>
      )}
    </>
  );

  return (
    <SafeViewportOverlay
      onClose={onClose}
      closing={closing}
      className="flex-col items-center"
    >
      <div
        className={`relative flex min-h-0 max-h-full w-full max-w-xl flex-1 flex-col overflow-hidden rounded-lg border border-gray-700/50 bg-surface-card shadow-xl sm:flex-none sm:max-h-[85dvh] md:max-w-4xl ${closing ? 'animate-scale-out' : 'animate-scale-in'}`}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Match details"
        aria-busy={visibleIsLoading}
      >
        {/* ── Scoreboard Header ── */}
        <div className="bg-primary-dark px-4 py-4 border-b border-gray-700">
          <button
            type="button"
            onClick={onClose}
            className="absolute right-1 top-1 flex h-11 w-11 items-center justify-center rounded-full text-2xl font-bold leading-none text-gray-400 transition-colors hover:bg-primary/50 hover:text-white active:bg-primary/50 active:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-yellow"
            aria-label="Close match detail modal"
          >
            &times;
          </button>

          <div className="flex items-center justify-center">
            {/* Home */}
            <div className="flex-1 text-center">
              <p className="text-2xl md:text-3xl font-heading font-bold text-white">{homeAbbr}</p>
            </div>

            {/* Score / Status */}
            <div className="flex-shrink-0 px-3 text-center min-w-[80px]">
              {liveScore ? (
                <>
                  <p className="text-2xl md:text-3xl font-bold tabular-nums text-accent-yellow">
                    {liveScore.home_score} - {liveScore.away_score}
                  </p>
                  <div className="mt-0.5">
                    {isLiveStatus ? (
                      <span className="inline-flex items-center text-badge text-green-400 font-semibold">
                        <span className="mr-1 h-1.5 w-1.5 rounded-full bg-green-500 motion-safe:animate-pulse"></span>
                        {liveScore.match_clock || 'LIVE'}
                      </span>
                    ) : (
                      <span className={`text-badge font-semibold ${isExceptionalStatus ? 'text-amber-300' : 'text-gray-400'}`}>
                        {isExceptionalStatus ? effectiveStatusCode : (liveScore.match_clock || effectiveStatusCode)}
                      </span>
                    )}
                  </div>
                </>
              ) : isExceptionalStatus ? (
                <span className="inline-flex min-h-6 items-center rounded-full bg-amber-500/15 px-2 text-badge font-bold text-amber-300 ring-1 ring-inset ring-amber-500/25">
                  {effectiveStatusCode}
                </span>
              ) : (
                <p className="text-lg text-gray-400 font-semibold">vs</p>
              )}
            </div>

            {/* Away */}
            <div className="flex-1 text-center">
              <p className="text-2xl md:text-3xl font-heading font-bold text-white">{awayAbbr}</p>
            </div>
          </div>
        </div>

        {/* ── Loading / Error / Empty States ── */}
        {visibleIsLoading && (
          <div className="flex items-center justify-center py-12" role="status">
            <div className="text-center">
              <div className="mb-2 inline-block h-8 w-8 rounded-full border-b-2 border-accent-yellow motion-safe:animate-spin"></div>
              <p className="text-gray-400 text-sm">Loading players...</p>
            </div>
          </div>
        )}

        {visiblePlayersError && !visibleIsLoading && (
          <div className="flex flex-col items-center justify-center gap-3 p-6 text-center" role="alert">
            <p className="text-secondary text-sm">{visiblePlayersError}</p>
            <button
              type="button"
              onClick={() => setRequestVersion(version => version + 1)}
              className="inline-flex min-h-10 items-center rounded-lg border border-gray-700 px-4 text-sm font-semibold text-gray-200 transition-colors duration-150 hover:border-accent-yellow hover:text-accent-yellow focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-yellow"
            >
              Try again
            </button>
          </div>
        )}

        {!visibleIsLoading && !visiblePlayersError && visibleMatchPlayers.length === 0 && (
          <div className="flex items-center justify-center p-4">
            <p className="text-gray-500 italic text-sm">No players found for this match.</p>
          </div>
        )}

        {/* ── Player Content (shown when data is loaded) ── */}
        {!visibleIsLoading && !visiblePlayersError && effectiveMatch && visibleMatchPlayers.length > 0 && (
          <>
            {/* ── DESKTOP: Two-column layout (md+) ── */}
            <div className="hidden md:block flex-1 overflow-hidden">
              {/* Column Headers */}
              <div className="grid grid-cols-2 border-b border-gray-700/50 bg-primary/20">
                <div className="px-2 py-1.5 border-r border-gray-700/30">
                  {renderColumnHeader(homeAbbr)}
                </div>
                <div className="px-2 py-1.5">
                  {renderColumnHeader(awayAbbr)}
                </div>
              </div>

              {/* Two-column player lists. Taller rows intentionally keep the
                  viewport focused on roughly ten players per team. */}
              <div className="grid grid-cols-2 overflow-y-auto max-h-[calc(85vh-180px)] overscroll-contain scrollbar-mobile-hide">
                <div className="border-r border-gray-700/30">
                  {renderPlayerList(homePlayers)}
                </div>
                <div>
                  {renderPlayerList(awayPlayers)}
                </div>
              </div>
            </div>

            {/* ── MOBILE: Toggle + single column (below md) ── */}
            <div className="md:hidden flex-1 overflow-hidden flex flex-col">
              {/* Team Toggle */}
              <div className="border-b border-accent-yellow/15 bg-primary-dark/80 p-2">
                <div className="grid grid-cols-2 gap-2">
                  <button
                    onClick={() => setSelectedTeamId(effectiveMatch.match_home_team_id)}
                    className={`min-h-10 rounded-lg border px-3 py-1.5 text-sm font-bold transition-colors duration-200 ${
                      selectedTeamId === effectiveMatch.match_home_team_id
                        ? 'border-accent-yellow bg-accent-yellow text-primary shadow-md'
                        : 'border-accent-yellow/25 bg-primary text-gray-300 hover:border-accent-yellow/60 hover:text-white active:border-accent-yellow/60 active:text-white'
                    }`}
                    aria-pressed={selectedTeamId === effectiveMatch.match_home_team_id}
                  >
                    {homeAbbr}
                  </button>
                  <button
                    onClick={() => setSelectedTeamId(effectiveMatch.match_away_team_id)}
                    className={`min-h-10 rounded-lg border px-3 py-1.5 text-sm font-bold transition-colors duration-200 ${
                      selectedTeamId === effectiveMatch.match_away_team_id
                        ? 'border-accent-yellow bg-accent-yellow text-primary shadow-md'
                        : 'border-accent-yellow/25 bg-primary text-gray-300 hover:border-accent-yellow/60 hover:text-white active:border-accent-yellow/60 active:text-white'
                    }`}
                    aria-pressed={selectedTeamId === effectiveMatch.match_away_team_id}
                  >
                    {awayAbbr}
                  </button>
                </div>
              </div>

              {/* Column Header */}
              <div className="border-b border-accent-light-blue/10 bg-primary/40 px-3 py-1.5">
                {renderColumnHeader(
                  selectedTeamId === effectiveMatch.match_home_team_id ? homeAbbr : awayAbbr,
                  true,
                )}
              </div>

              {/* Single-column player list */}
              <div className="flex-1 overflow-y-auto overscroll-contain scrollbar-mobile-hide">
                {renderPlayerList(mobileDisplayedPlayers)}
              </div>
            </div>
          </>
        )}
      </div>
    </SafeViewportOverlay>
  );
};

export default MatchBreakdownModal;
